import { createAuthEventBuffer } from './auth-event-buffer';
import { settleByDeadline } from './auth-bootstrap-deadline';
import { readNativeAuthSessionState } from './auth-persistence';
import {
  applyQuarantinedAuthEvent,
  applySuccessfulCallback,
  createStoredSessionQuarantine,
  quarantineFailedCallback,
} from './auth-subscription-quarantine';
import { isSameAuthSession, prepareNativeAuthBootstrap } from './native-auth-bootstrap';
import {
  acknowledgeNativeAuthCallbackOutcome,
  completeNativeAuthCallback,
  getNativeAuthLaunchDisposition,
  recordNativeAuthSessionApplied,
  waitForNativeAuthCallbackOutcome,
  waitForNativeAuthCallbackStart,
} from './native-auth-launch';
import { registerNativeAuthSubscription } from './native-auth-subscription-readiness';

import type { QueryClient } from '@tanstack/react-query';
import type { Session, SupabaseClient } from '@supabase/supabase-js';
import type { BufferedAuthEvent } from './auth-event-buffer';
import type { NativeAuthSessionRead } from './auth-persistence';
import type { ApplyAuthStateChange } from './auth-subscription-quarantine';
import type { DeadlineResult } from './auth-bootstrap-deadline';
import type { NativeAuthCallbackOutcome } from './native-auth-launch';

const BOOTSTRAP_DEADLINE_MS = 2000;
const AUTH_STORAGE_SNAPSHOT_DEADLINE_MS = 1000;
const CALLBACK_START_GRACE_MS = 500;
const CALLBACK_OUTCOME_DEADLINE_MS = 30_000;
const CALLBACK_CLEANUP_DEADLINE_MS = 250;

interface AuthSubscriptionBootstrapOptions {
  readonly supabase: SupabaseClient;
  readonly queryClient: QueryClient;
  readonly applyAuthStateChange: ApplyAuthStateChange;
  readonly onBootstrapSession: (session: Session) => void;
  readonly onSuppressedInitialSession: () => void;
  readonly onInitializationError: () => void;
}

function bufferedEventsRejectBootstrap(
  buffered: readonly BufferedAuthEvent[],
  storedSession: Session,
): boolean {
  return buffered.some(({ event, session }) => {
    if (event === 'SIGNED_OUT') {
      return true;
    }
    if (session) {
      return !isSameAuthSession(storedSession, session);
    }
    return event !== 'INITIAL_SESSION';
  });
}

function resolveStoredSessionRead(
  result: DeadlineResult<NativeAuthSessionRead>,
  onError: () => void,
): NativeAuthSessionRead {
  if (result.status === 'failed' || result.status === 'timed-out') {
    onError();
  }
  return result.status === 'settled' ? result.value : { status: 'unavailable' };
}

/** Coordinate launch classification before auth-js can recover the wrong account. */
export function startAuthSubscriptionBootstrap({
  supabase,
  queryClient,
  applyAuthStateChange,
  onBootstrapSession,
  onSuppressedInitialSession,
  onInitializationError,
}: AuthSubscriptionBootstrapOptions): () => void {
  let active = true;
  let bootstrapMayCommit = true;
  let unsubscribe: (() => void) | undefined;
  const bootstrapAbort = new AbortController();
  const quarantine = createStoredSessionQuarantine();
  const readiness = registerNativeAuthSubscription();

  const events = createAuthEventBuffer(async (event, session) => {
    if (!active) {
      return;
    }
    let applied = false;
    await applyQuarantinedAuthEvent(
      event,
      session,
      quarantine,
      async (...args) => {
        await applyAuthStateChange(...args);
        applied = true;
      },
      onSuppressedInitialSession,
    );
    if (applied) {
      recordNativeAuthSessionApplied(session);
    }
  });

  const subscribe = () => {
    if (!active || unsubscribe) {
      return;
    }
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      events.push(event, session).catch(onInitializationError);
    });
    unsubscribe = () => subscription.unsubscribe();
  };

  const releaseEvents = async (): Promise<boolean> => {
    try {
      await events.release();
      return true;
    } catch {
      onInitializationError();
      return false;
    }
  };

  const markReadyAfterRelease = (released: boolean): void => {
    if (active && released) {
      readiness.markReady();
    }
  };

  const initializeOrdinaryLaunch = async (allowBootstrap: boolean): Promise<void> => {
    subscribe();
    if (allowBootstrap) {
      const preparation = prepareNativeAuthBootstrap(
        supabase,
        queryClient,
        () => active && bootstrapMayCommit,
      );
      const result = await settleByDeadline(
        preparation,
        BOOTSTRAP_DEADLINE_MS,
        bootstrapAbort.signal,
      );
      if (result.status === 'timed-out') {
        bootstrapMayCommit = false;
      } else if (result.status === 'failed') {
        onInitializationError();
      } else if (
        result.status === 'settled' &&
        result.value &&
        active &&
        !bufferedEventsRejectBootstrap(events.snapshot(), result.value)
      ) {
        onBootstrapSession(result.value);
      }
    }
    const released = await releaseEvents();
    markReadyAfterRelease(released);
  };

  const initializeCallbackLaunch = async (): Promise<void> => {
    const waitForDeliveredOutcome = async (): Promise<NativeAuthCallbackOutcome | null> => {
      try {
        return await waitForNativeAuthCallbackOutcome(
          CALLBACK_OUTCOME_DEADLINE_MS,
          bootstrapAbort.signal,
        );
      } catch {
        onInitializationError();
        return null;
      }
    };
    const [storedResult, deliveredOutcome] = await Promise.all([
      settleByDeadline<NativeAuthSessionRead>(
        readNativeAuthSessionState(),
        AUTH_STORAGE_SNAPSHOT_DEADLINE_MS,
        bootstrapAbort.signal,
      ),
      waitForDeliveredOutcome(),
    ]);
    const storedRead = resolveStoredSessionRead(storedResult, onInitializationError);
    let outcome = deliveredOutcome;

    if (!active) {
      return;
    }
    if (!outcome) {
      const fallbackOutcome = { status: 'failure' } as const;
      const cleanup = await settleByDeadline(
        completeNativeAuthCallback(fallbackOutcome),
        CALLBACK_CLEANUP_DEADLINE_MS,
        bootstrapAbort.signal,
      );
      if (cleanup.status === 'failed' || cleanup.status === 'timed-out') {
        onInitializationError();
      }
      if (!active) {
        return;
      }
      outcome = await waitForNativeAuthCallbackOutcome(0, bootstrapAbort.signal);
    }

    if (outcome?.status === 'success') {
      await applySuccessfulCallback(outcome, quarantine, applyAuthStateChange);
    } else {
      quarantineFailedCallback(storedRead, quarantine, onSuppressedInitialSession);
    }
    if (!active) {
      return;
    }

    subscribe();
    const released = await releaseEvents();
    if (active && released && outcome) {
      acknowledgeNativeAuthCallbackOutcome(outcome);
    }
    markReadyAfterRelease(released);
  };

  const initialize = async (): Promise<void> => {
    try {
      const disposition = await getNativeAuthLaunchDisposition();
      if (!active) {
        return;
      }
      if (disposition === 'callback') {
        await initializeCallbackLaunch();
        return;
      }
      if (disposition === 'unknown') {
        const callbackStarted = await waitForNativeAuthCallbackStart(
          CALLBACK_START_GRACE_MS,
          bootstrapAbort.signal,
        );
        if (!active) {
          return;
        }
        if (callbackStarted) {
          await initializeCallbackLaunch();
          return;
        }
        await initializeOrdinaryLaunch(false);
        return;
      }
      await initializeOrdinaryLaunch(true);
    } catch {
      if (active) {
        onInitializationError();
        subscribe();
        const released = await releaseEvents();
        markReadyAfterRelease(released);
      }
    }
  };
  initialize().catch(() => {
    if (active) {
      onInitializationError();
    }
  });

  return () => {
    active = false;
    bootstrapMayCommit = false;
    bootstrapAbort.abort();
    unsubscribe?.();
    readiness.stop();
  };
}
